import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createAnnouncement,
  deleteAnnouncement,
  deleteMaterial,
  getLeaderboard,
  listAnnouncements,
  listMaterials,
  uploadMaterial,
} from '@/api/classes';
import { Button } from '@/components/ui/button/button';

import type {
  ClassAnnouncementItem,
  ClassMaterialItem,
  ClassroomOut,
  LeaderboardResponse,
} from '@/types';

import styles from './class-detail-dialog.module.css';

type TabKey = 'announcements' | 'leaderboard' | 'materials';

function formatDateTime(iso: string): string {
  if (!iso) return '';
  return iso.replace('T', ' ').slice(0, 16);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export interface ClassDetailDialogProps {
  classroom: ClassroomOut;
  /** 教师视角可发布公告/上传资料，且排行榜显示真实姓名 */
  isTeacher: boolean;
  onClose: () => void;
}

export function ClassDetailDialog({ classroom, isTeacher, onClose }: ClassDetailDialogProps) {
  const [tab, setTab] = useState<TabKey>('announcements');
  const queryClient = useQueryClient();
  const [toast, setToast] = useState('');
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  };

  // 公告
  const announcementsQuery = useQuery({
    queryKey: ['class-announcements', classroom.id],
    queryFn: () => listAnnouncements(classroom.id),
  });
  const [annTitle, setAnnTitle] = useState('');
  const [annContent, setAnnContent] = useState('');
  const [annError, setAnnError] = useState('');
  const [publishing, setPublishing] = useState(false);

  // 排行榜
  const leaderboardQuery = useQuery({
    queryKey: ['class-leaderboard', classroom.id],
    queryFn: () => getLeaderboard(classroom.id),
  });

  // 资料
  const materialsQuery = useQuery({
    queryKey: ['class-materials', classroom.id],
    queryFn: () => listMaterials(classroom.id),
  });
  const [matTitle, setMatTitle] = useState('');
  const [matDesc, setMatDesc] = useState('');
  const [matFile, setMatFile] = useState<File | null>(null);
  const [matError, setMatError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['class-announcements', classroom.id] });
    queryClient.invalidateQueries({ queryKey: ['class-materials', classroom.id] });
    queryClient.invalidateQueries({ queryKey: ['class-leaderboard', classroom.id] });
    queryClient.invalidateQueries({ queryKey: ['my-classes'] });
    queryClient.invalidateQueries({ queryKey: ['joined-classes'] });
  };

  const handlePublish = async () => {
    if (!annTitle.trim() || !annContent.trim()) {
      setAnnError('标题与内容均不能为空');
      return;
    }
    setPublishing(true);
    try {
      await createAnnouncement(classroom.id, {
        title: annTitle.trim(),
        content: annContent.trim(),
      });
      setAnnTitle('');
      setAnnContent('');
      setAnnError('');
      showToast('公告已发布');
      queryClient.invalidateQueries({ queryKey: ['class-announcements', classroom.id] });
      queryClient.invalidateQueries({ queryKey: ['my-classes'] });
    } catch (err) {
      setAnnError(err instanceof Error ? err.message : '发布失败');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteAnnouncement = async (item: ClassAnnouncementItem) => {
    if (!window.confirm(`确定删除公告「${item.title}」吗？`)) return;
    try {
      await deleteAnnouncement(classroom.id, item.id);
      showToast('已删除');
      queryClient.invalidateQueries({ queryKey: ['class-announcements', classroom.id] });
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleUpload = async () => {
    if (!matTitle.trim()) {
      setMatError('资料标题不能为空');
      return;
    }
    if (!matFile) {
      setMatError('请选择要上传的文件');
      return;
    }
    setUploading(true);
    try {
      await uploadMaterial(classroom.id, {
        title: matTitle.trim(),
        description: matDesc.trim(),
        file: matFile,
      });
      setMatTitle('');
      setMatDesc('');
      setMatFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMatError('');
      showToast('资料已上传');
      queryClient.invalidateQueries({ queryKey: ['class-materials', classroom.id] });
      queryClient.invalidateQueries({ queryKey: ['my-classes'] });
    } catch (err) {
      setMatError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMaterial = async (item: ClassMaterialItem) => {
    if (!window.confirm(`确定删除资料「${item.title}」吗？`)) return;
    try {
      await deleteMaterial(classroom.id, item.id);
      showToast('已删除');
      queryClient.invalidateQueries({ queryKey: ['class-materials', classroom.id] });
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败');
    }
  };

  const leaderboard = leaderboardQuery.data as LeaderboardResponse | undefined;
  const announcements = announcementsQuery.data ?? [];
  const materials = materialsQuery.data ?? [];

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'announcements', label: `公告 (${announcements.length})` },
    { key: 'leaderboard', label: '作业排行榜' },
    { key: 'materials', label: `资料区 (${materials.length})` },
  ];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>{classroom.name}</h3>
            <div className={styles.meta}>
              {(classroom.teachers ?? []).length > 0
                ? `教师团队：${(classroom.teachers ?? [])
                    .map((t) => t.display_name + (t.is_owner ? '（创建者）' : ''))
                    .join('、')}`
                : `授课教师：${classroom.teacher_name || '—'}`}
              {' · '}
              {classroom.member_count} 名学生 · {classroom.assignment_count} 项作业
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </div>

        <div className={styles.tabs}>
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          {/* 公告 */}
          {tab === 'announcements' && (
            <div className={styles.section}>
              {isTeacher && (
                <div className={styles.publishBox}>
                  <div className={styles.publishTitle}>发布新公告</div>
                  <input
                    className={styles.input}
                    placeholder="公告标题，如：期中复习安排"
                    value={annTitle}
                    onChange={(e) => {
                      setAnnTitle(e.target.value);
                      if (annError) setAnnError('');
                    }}
                  />
                  <textarea
                    className={styles.textarea}
                    placeholder="公告内容（班级学生会收到通知）"
                    value={annContent}
                    onChange={(e) => {
                      setAnnContent(e.target.value);
                      if (annError) setAnnError('');
                    }}
                    rows={3}
                  />
                  {annError && <div className={styles.error}>{annError}</div>}
                  <div className={styles.publishActions}>
                    <Button onClick={handlePublish} loading={publishing}>
                      发布公告
                    </Button>
                  </div>
                </div>
              )}
              {announcementsQuery.isLoading ? (
                <div className={styles.empty}>加载中...</div>
              ) : announcements.length === 0 ? (
                <div className={styles.empty}>
                  {isTeacher ? '还没有发布公告，发一条试试。' : '老师暂未发布班级公告。'}
                </div>
              ) : (
                <div className={styles.annList}>
                  {announcements.map((a) => (
                    <div key={a.id} className={styles.annCard}>
                      <div className={styles.annHead}>
                        <span className={styles.annTitle}>{a.title}</span>
                        <span className={styles.annTime}>{formatDateTime(a.created_at)}</span>
                      </div>
                      <p className={styles.annContent}>{a.content}</p>
                      <div className={styles.annFoot}>
                        <span className={styles.annAuthor}>发布：{a.teacher_name}</span>
                        {a.can_delete && isTeacher && (
                          <Button
                            variant="danger"
                            size="small"
                            onClick={() => handleDeleteAnnouncement(a)}
                          >
                            删除
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 排行榜 */}
          {tab === 'leaderboard' && (
            <div className={styles.section}>
              {leaderboardQuery.isLoading ? (
                <div className={styles.empty}>加载中...</div>
              ) : !leaderboard || leaderboard.items.length === 0 ? (
                <div className={styles.empty}>暂无排行数据</div>
              ) : (
                <>
                  <div className={styles.lbSummary}>
                    <span>
                      {leaderboard.summary.student_count} 名学生 ·{' '}
                      {leaderboard.summary.job_count} 项作业
                    </span>
                    <span>
                      班级平均分：
                      {leaderboard.summary.class_avg !== null
                        ? leaderboard.summary.class_avg
                        : '—'}
                    </span>
                    <span>
                      班级完成率：
                      {Math.round(leaderboard.summary.class_completion * 100)}%
                    </span>
                  </div>
                  {leaderboard.anonymous && (
                    <div className={styles.anonTip}>
                      🛡️ 已开启匿名模式：除你以外的同学均以匿名展示，保护大家的学习心态。
                    </div>
                  )}
                  <table className={styles.lbTable}>
                    <thead>
                      <tr>
                        <th>排名</th>
                        <th>{leaderboard.anonymous ? '同学' : '学生'}</th>
                        <th>平均分</th>
                        <th>最高分</th>
                        <th>完成率</th>
                        <th>已提交</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.items.map((item) => (
                        <tr
                          key={item.student_id}
                          className={item.display_name.includes('（我）') ? styles.selfRow : ''}
                        >
                          <td>
                            <span
                              className={`${styles.rankBadge} ${
                                item.rank <= 3 ? styles[`rank${item.rank}`] ?? '' : ''
                              }`}
                            >
                              {item.rank}
                            </span>
                          </td>
                          <td>
                            <div className={styles.studentCell}>
                              <span>{item.display_name}</span>
                              {!leaderboard.anonymous && item.username && (
                                <span className={styles.studentSub}>{item.username}</span>
                              )}
                            </div>
                          </td>
                          <td className={styles.scoreCell}>
                            {item.avg_score !== null ? item.avg_score : '—'}
                          </td>
                          <td className={styles.scoreCell}>
                            {item.best_score !== null ? item.best_score : '—'}
                          </td>
                          <td>
                            <div className={styles.completionWrap}>
                              <div className={styles.completionBar}>
                                <div
                                  className={styles.completionFill}
                                  style={{ width: `${Math.min(item.completion, 1) * 100}%` }}
                                />
                              </div>
                              <span className={styles.completionText}>
                                {Math.round(item.completion * 100)}%
                              </span>
                            </div>
                          </td>
                          <td className={styles.scoreCell}>
                            {item.submitted}/{item.total_jobs}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* 资料区 */}
          {tab === 'materials' && (
            <div className={styles.section}>
              {isTeacher && (
                <div className={styles.publishBox}>
                  <div className={styles.publishTitle}>上传班级资料</div>
                  <input
                    className={styles.input}
                    placeholder="资料标题，如：第一章知识点梳理"
                    value={matTitle}
                    onChange={(e) => {
                      setMatTitle(e.target.value);
                      if (matError) setMatError('');
                    }}
                  />
                  <input
                    className={styles.input}
                    placeholder="资料说明（选填）"
                    value={matDesc}
                    onChange={(e) => setMatDesc(e.target.value)}
                  />
                  <input
                    ref={fileInputRef}
                    className={styles.fileInput}
                    type="file"
                    onChange={(e) => {
                      setMatFile(e.target.files?.[0] ?? null);
                      if (matError) setMatError('');
                    }}
                  />
                  {matError && <div className={styles.error}>{matError}</div>}
                  <div className={styles.publishActions}>
                    <Button onClick={handleUpload} loading={uploading}>
                      上传资料
                    </Button>
                  </div>
                </div>
              )}
              {materialsQuery.isLoading ? (
                <div className={styles.empty}>加载中...</div>
              ) : materials.length === 0 ? (
                <div className={styles.empty}>
                  {isTeacher ? '还没有上传资料，上传一份课件试试。' : '老师暂未上传班级资料。'}
                </div>
              ) : (
                <div className={styles.matList}>
                  {materials.map((m) => (
                    <div key={m.id} className={styles.matCard}>
                      <div className={styles.matIcon}>📎</div>
                      <div className={styles.matInfo}>
                        <div className={styles.matTitle}>{m.title}</div>
                        {m.description && <div className={styles.matDesc}>{m.description}</div>}
                        <div className={styles.matMeta}>
                          {m.file_name} · {formatSize(m.file_size)} · {m.teacher_name} ·{' '}
                          {formatDateTime(m.created_at)}
                        </div>
                      </div>
                      <div className={styles.matActions}>
                        <a
                          className={styles.downloadLink}
                          href={m.download_url}
                          download={m.file_name}
                          target="_blank"
                          rel="noreferrer"
                        >
                          下载
                        </a>
                        {m.can_delete && isTeacher && (
                          <Button
                            variant="danger"
                            size="small"
                            onClick={() => handleDeleteMaterial(m)}
                          >
                            删除
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            关闭
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              refreshAll();
              showToast('已刷新');
            }}
          >
            刷新数据
          </Button>
        </div>
      </div>

      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
}
